import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { backendUrl } from "../utils/urlHelper";

import * as XLSX from "xlsx";

const AddUserPage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    username: "",
    name: "",
    branch: "",
    password: "",
    role: "",
    secondaryBranches: [],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [branches, setBranches] = useState([]);

  const [file, setFile] = useState(null);
  const [bulkUsers, setBulkUsers] = useState([]); // Will store parsed Excel data

  const token = localStorage.getItem("token"); // Retrieve token from local storage

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(backendUrl + "/api/list/branches/");
      setBranches(response?.data?.branches);
    } catch (err) {
      console.error("Failed to fetch users");
    }
  };

  const resetUserData = () => {
    setUserData({
      username: "",
      name: "",
      branch: "",
      password: "",
      role: "",
      secondaryBranches: [],
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");

    // Validation
    if (
      !userData.username ||
      !userData.name ||
      !userData.branch ||
      !userData.password ||
      !userData.role
    ) {
      alert("Please fill all the fields");
      return;
    }

    if (!/^\d{10}$/.test(userData.username)) {
      alert("Please provide valid 10-digit mobile number");
      return;
    }

    if (userData.password?.length < 6) {
      alert("Password should have atleast 6 characters");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        backendUrl + "/api/auth/register",
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      setMessage("User added successfully!");
      resetUserData();
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to add user. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // -- Bulk upload handlers --
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
  };

  const parseExcelFile = async () => {
    if (!file) {
      alert("Please select an Excel file first.");
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (evt) => {
        // Parse data
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: "binary" });
        // Assume first sheet is the relevant one
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const cleanedData = jsonData.map((row) => {
          const cleanedRow = {};
          for (let [key, value] of Object.entries(row)) {
            // Trim the key
            const trimmedKey = key.trim();

            // If the value is a string, trim it
            if (typeof value === "string") {
              value = value.trim();
            }

            // Assign the cleaned data to the new object
            cleanedRow[trimmedKey] = value;
          }
          return cleanedRow;
        });
        /*
          jsonData will be an array of objects, e.g.:
          [
            { username: '1234567890', name: 'John Doe', branch: 'XYZ', ... },
            ...
          ]
        */
        setBulkUsers(cleanedData);
        console.log(cleanedData);
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      console.error("Error parsing Excel file:", error);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkUsers.length === 0) {
      alert("No data found to upload. Please parse the file first.");
      return;
    }

    // Optional: Validate each row before sending to the server
    // e.g., username must be 10-digit phone, password length >=6, etc.
    for (let user of bulkUsers) {
      if (!user.name) {
        alert(`Name not provided for one or more users in the file`);
        return;
      }

      if (!/^\d{10}$/.test(user.mobile)) {
        alert(`Invalid username (mobile) for user: ${user.name}`);
        return;
      }
      if (!branches.includes(user.branch)) {
        alert(`Invalid branch provided for user: ${user.name}`);
        return;
      }
      // Add more validations as needed
    }

    setLoading(true);
    setMessage("");
    try {
      const response = await axios.post(
        `${backendUrl}/api/auth/bulkRegister`,
        { users: bulkUsers },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      setMessage(
        response?.data?.message || "Bulk users uploaded successfully!"
      );
      setBulkUsers([]);
      setFile(null);
    } catch (error) {
      console.error(error);
      setMessage(
        error.response?.data?.message ||
          "Failed to add users in bulk. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ textAlign: "center", mb: 4 }}>
        Add New User
      </Typography>

      <Box
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          backgroundColor: "#f9f9f9",
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
        }}
        onSubmit={handleSubmit}
      >
        {/* Username (Phone Number) */}
        <TextField
          label="Phone Number (Username)"
          variant="outlined"
          type="tel"
          name="username"
          value={userData.username}
          onChange={handleChange}
          fullWidth
          required
        />

        {/* Name */}
        <TextField
          label="Full Name"
          variant="outlined"
          name="name"
          value={userData.name}
          onChange={handleChange}
          fullWidth
          required
        />

        {/* Branch */}
        <FormControl fullWidth>
          <InputLabel>Branch</InputLabel>
          <Select
            label="Branch"
            value={userData.branch}
            name="branch"
            onChange={handleChange}
            required
          >
            {branches.map((branch, index) => (
              <MenuItem key={index} value={branch}>
                {branch}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Password */}
        <TextField
          label="Password"
          variant="outlined"
          type="password"
          name="password"
          value={userData.password}
          onChange={handleChange}
          fullWidth
          required
        />

        {/* Role */}
        <FormControl fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={userData.role}
            name="role"
            onChange={handleChange}
            required
          >
            <MenuItem value="staff">Staff</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </FormControl>

        {/* Secondary Branches */}
        {userData.role === "manager" && (
          <FormControl fullWidth>
            <InputLabel>Secondary Branches</InputLabel>
            <Select
              label="Secondary Branches"
              multiple
              value={userData.secondaryBranches}
              name="secondaryBranches"
              onChange={handleChange}
              renderValue={(selected) => selected.join(", ")} // Display selected branches
            >
              {branches.map((branch, index) => (
                <MenuItem
                  key={index}
                  value={branch}
                  sx={{
                    backgroundColor: userData.secondaryBranches.includes(branch)
                      ? "rgba(0, 0, 255, 0.1)" // Light blue background for selected items
                      : "inherit",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(0, 0, 255, 0.2) !important", // Slightly darker blue when selected
                      color: "#000", // Ensure text is black for better readability
                    },
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 255, 0.2)", // Hover effect
                    },
                  }}
                >
                  {branch}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Submit Button */}
        <Button
          variant="contained"
          type="submit"
          sx={{
            backgroundColor: "#3B82F6",
            padding: "0.75rem",
            fontWeight: "bold",
            textTransform: "none",
            fontSize: "1rem",
          }}
        >
          {loading ? "Adding User..." : "Add User"}
        </Button>
        {/* Message */}
        {message && (
          <Typography
            sx={{
              textAlign: "center",
              color: message.includes("successfully") ? "green" : "red",
              my: 1,
            }}
          >
            {message}
          </Typography>
        )}
        <Button
          variant="outlined"
          sx={{
            backgroundColor: "#eeeeee",
            padding: "0.75rem",
            textTransform: "none",
          }}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Box>

      {/* ========== Bulk Upload Section ========== */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          my: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Bulk Upload Users from Excel
        </Typography>
        <input
          type="file"
          accept=".xlsx, .xls, .csv"
          onChange={handleFileChange}
          style={{ marginBottom: "1rem" }}
        />
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={parseExcelFile}
            disabled={!file}
            sx={{
              textTransform: "none",
            }}
          >
            Parse File
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkUpload}
            disabled={bulkUsers.length === 0}
            sx={{
              backgroundColor: "#3B82F6",
              textTransform: "none",
            }}
          >
            {loading ? "Uploading..." : "Upload Users"}
          </Button>
        </Box>

        {bulkUsers.length > 0 && (
          <Typography sx={{ mb: 1 }}>
            Parsed {bulkUsers.length} user(s). Ready to upload.
          </Typography>
        )}

        {/* Message (Bulk) */}
        {message && (
          <Typography
            sx={{
              textAlign: "center",
              color: message.includes("successfully") ? "green" : "red",
              my: 1,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default AddUserPage;
